/**
 * Marker shapes for leader lines on hotspots and portals.
 *
 * A leader line runs from a marker to a target point. Its start is pushed out
 * from the marker's centre to the visible edge of what the marker actually
 * renders, so the line neither starts inside a big marker nor floats away from
 * a small one. These helpers decide that shape from the same fields
 * createSingleHotspot / createSinglePortal branch on.
 */

import { hasRenderableMarkerStack, type MarkerLayerInput } from './markerStack';

/** sphere: radius 0.1 × scale (hotspot and portal spheres, and the media fallback sphere).
 *  globe:  radius 0.125 × scale (360° panorama portal).
 *  plane:  image / video / gif plane, half-extent 0.5 × scale.
 *  stack:  stacked marker; the line leaves the edge of the stack's bounds (markerStackEdgeDistance),
 *          or 0.5 × scale like a plane while the layout is not known yet. */
export type LeaderLineMarkerShape = 'sphere' | 'globe' | 'plane' | 'stack';

type ScaleLike = number | { x?: number; y?: number; z?: number; _x?: number; _y?: number; _z?: number } | null | undefined;

type MarkerStackLike = ReadonlyArray<MarkerLayerInput | null | undefined> | null;

export interface PortalMarkerLike {
    type?: string;
    menuOnly?: boolean;
    imageUrl?: string;
    videoUrl?: string;
    gifUrl?: string;
    markerStack?: MarkerStackLike;
}

/**
 * Shape a hotspot's leader line anchors to: spheres are spheres, everything else is a plane
 * (the original rule), except a stacked marker: its stack, or the fallback sphere when it
 * has no drawable layer yet.
 */
export function hotspotLeaderLineShape(hotspot: { type?: string; markerStack?: MarkerStackLike }): LeaderLineMarkerShape {
    if (hotspot.type === 'stack') return hasRenderableMarkerStack(hotspot.markerStack) ? 'stack' : 'sphere';
    return hotspot.type === 'sphere' ? 'sphere' : 'plane';
}

/**
 * Shape createSinglePortal renders for a portal. A media portal whose media URL is
 * missing renders the fallback sphere; the invisible collision portal is a plane.
 */
export function portalMarkerShape(portal: PortalMarkerLike): LeaderLineMarkerShape {
    if (portal.type === 'panorama') return 'globe';
    if (portal.type === 'stack') return hasRenderableMarkerStack(portal.markerStack) ? 'stack' : 'sphere';
    if (portal.type === 'plane'
        || (portal.type === 'image' && portal.imageUrl)
        || (portal.type === 'video' && portal.videoUrl)
        || (portal.type === 'gif' && portal.gifUrl)) {
        return 'plane';
    }
    return 'sphere';
}

/**
 * Whether a portal's marker is drawn in its `color`: the colour sphere (the Sphere marker, and
 * the fallback sphere a media or stack portal shows while it has no media). Image, video, GIF
 * and stack markers show their media, the 360° globe has its own look, and the invisible plane
 * is not drawn, so none of them use the colour.
 */
export function portalMarkerUsesColor(portal: PortalMarkerLike): boolean {
    return portalMarkerShape(portal) === 'sphere';
}

/**
 * Shape a portal's leader line anchors to, or null when the portal draws no line.
 * Menu-only portals have no 3D presence, and invisible collision planes have no
 * visible marker (the editor hides the leader-line controls for them), so neither
 * gets a line.
 */
export function portalLeaderLineShape(portal: PortalMarkerLike): LeaderLineMarkerShape | null {
    if (portal.menuOnly || portal.type === 'plane') return null;
    return portalMarkerShape(portal);
}

/** Plane-framed markers (image / video / GIF planes and stacks) share the plane transform:
 *  local scale (x, z, y) and the 90° face-forward rotation. */
export function isPlaneFramedShape(shape: LeaderLineMarkerShape | null): boolean {
    return shape === 'plane' || shape === 'stack';
}

/** Distance from the marker centre to where the line starts, in world units. */
export function leaderLineStartGap(shape: LeaderLineMarkerShape, scale: ScaleLike, lineRadius: number): number {
    const raw = scale || 1;
    const s = typeof raw === 'number' ? { x: raw, y: raw, z: raw } : raw;
    const largest = Math.max(
        Math.abs(s._x ?? s.x ?? 1),
        Math.abs(s._y ?? s.y ?? 1),
        Math.abs(s._z ?? s.z ?? 1),
    );
    // Spheres get a little clearance past the surface (radius 0.1 → 0.12, 0.125 → 0.15).
    const perUnitScale = shape === 'sphere' ? 0.12 : shape === 'globe' ? 0.15 : 0.5;
    return largest * perUnitScale + lineRadius * 2;
}
